/**
 * The one piece of dev machinery that is always in the bundle: the chord listener.
 *
 * `AppComponent` renders this next to the shell, unconditionally. It is about twenty lines
 * and one `keydown` handler, and it must be present even when dev mode is off — otherwise
 * the chord that turns dev mode on has nobody listening for it, and the only way in is to
 * type `?dev=1`, which defeats the point of having a chord.
 *
 * Everything else is behind `@defer (when dev.enabled())`: the overlay, the five panels,
 * the registry, `SwitchBoard` and the `--proto-*` styles all land in a lazy chunk that a
 * normal visitor never downloads. `@defer` rather than a route because the overlay sits
 * *over* whatever screen is showing — it is not a page you navigate to.
 *
 * ### The chord
 *
 * Ctrl/⌘ + Alt + D. `event.code === 'KeyD'` rather than `event.key`, because Alt on macOS
 * turns D into `∂` and the chord would silently stop working. Nothing in the product uses
 * it and no browser claims it on either platform.
 *
 * Either modifier is accepted, and on macOS that matters: ⌘ + ⌥ + D is the system shortcut
 * for hiding the Dock, so macOS swallows it before the page sees it. **Ctrl + ⌥ + D** is the
 * one to document for Mac. `?dev=1` reaches the same state without a chord at all, which is
 * what the E2E specs use.
 *
 * ### The marks are not here
 *
 * `orange-anno-marks` is placed by the shell, inside the page's content column, because
 * the marks belong at the head of the screen they annotate. This component is a sibling of
 * the shell and hosts only the overlay.
 */
import { ChangeDetectionStrategy, Component, HostListener, inject } from '@angular/core';
import { DevMode } from '../core/dev/dev-mode';
import { DevOverlayComponent } from './overlay/dev-overlay.component';

@Component({
  selector: 'orange-dev-host',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DevOverlayComponent],
  /*
   * The inner @if is not redundant. @defer fetches its chunk once and never unrenders, so
   * without it, turning dev mode off would leave the overlay on screen — the chunk stays
   * loaded, which is what we want, but the panel has to go.
   */
  template: `
    @defer (when dev.enabled()) {
      @if (dev.enabled()) {
        <orange-dev-overlay />
      }
    }
  `,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
export class DevHostComponent {
  protected readonly dev = inject(DevMode);

  @HostListener('window:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (!event.altKey) return;
    if (!event.ctrlKey && !event.metaKey) return;
    if (event.code !== 'KeyD') return;
    event.preventDefault();
    this.dev.toggle();
  }
}
